import { App, Col, Form, type FormInstance, Input, Modal, Row, Select } from 'antd';
import { useEffect } from 'react';
import type { ProdutoLocalEstoque } from '@/features/produto-local-estoque/types/ProdutoLocalEstoque';
import type { FormProdutoValues } from '@/features/produto/types/FormProdutoValues';
import type { OrdenarLotes } from '@/features/produto/types/OrdenarLotes';
import type { ProdutoBodyEstoque, ProdutoBodyLote } from '@/features/produto/types/ProdutoBody';
import type { Dialog } from '@/std/dialog/types';
import { PickDate } from '@/components/date/PickDate';
import { ProdutoLoteStatus } from '@/features/produto/types/ProdutoLote';
import { DATE_FORMAT } from '@/std/const/date';
import { ProdutoTabEstoqueLoteModalEstoques } from './ProdutoTabEstoqueLoteModalEstoques';

export function ProdutoTabEstoqueLoteModal({
    dialog,
    form,
    locais,
    ordenarLotes,
    setFormDirty,
}: {
    dialog: Dialog<ProdutoBodyLote>;
    form: FormInstance<FormProdutoValues>;
    locais: ProdutoLocalEstoque[];
    ordenarLotes: OrdenarLotes;
    setFormDirty: (dirty: boolean) => void;
}) {
    const { notification } = App.useApp();

    const [formLote] = Form.useForm<ProdutoBodyLote>();
    const produtoEstoque: ProdutoBodyEstoque[] = Form.useWatch('produto_estoque', form) ?? [];

    const onFinish = (values: ProdutoBodyLote): void => {
        const lotes: ProdutoBodyLote[] = form.getFieldValue('lote') ?? [];
        const key = dialog.state.data?.key;

        if (dialog.state.mode === 'edit' && key) {
            const index = lotes.findIndex((lote) => lote.key === key);
            if (index === -1) {
                notification.error({ title: 'Lote não encontrado' });
                return;
            }

            const novosLotes = [...lotes];
            novosLotes[index] = values;
            form.setFieldValue('lote', novosLotes);
        }

        if (dialog.state.mode === 'add') {
            form.setFieldValue('lote', [...lotes, values]);
        }

        ordenarLotes();
        setFormDirty(true);
        void form.validateFields(['produto_estoque']).catch(() => undefined);
        dialog.actions.close();
    };

    useEffect(() => {
        if (!dialog.state.open) {
            return;
        }

        formLote.resetFields();

        if (dialog.state.data) {
            formLote.setFieldsValue(dialog.state.data);
        }
    }, [dialog.state.data, dialog.state.open, formLote]);

    return (
        <Modal
            onCancel={() => dialog.actions.close()}
            onOk={() => formLote.submit()}
            open={dialog.state.open}
            title={`${dialog.state.mode === 'add' ? 'Adicionar' : 'Editar'} lote`}
            width={800}
        >
            <Form form={formLote} layout='vertical' onFinish={onFinish}>
                <Form.Item hidden={true} name='key' />
                <Form.Item hidden={true} name='prl_idpk' />
                <Form.Item hidden={true} name='excluir' />

                <Row gutter={[8, 8]}>
                    <Col md={12} xs={24}>
                        <Form.Item
                            label='Código do lote'
                            name='prl_chave'
                            rules={[
                                { message: 'Por favor, forneça o código do lote', required: true },
                            ]}
                        >
                            <Input />
                        </Form.Item>
                    </Col>

                    <Col md={12} xs={24}>
                        <Form.Item
                            label='Localização'
                            name='prl_localizacao'
                            tooltip='Preencha a localização física deste lote e o seu número de identificação.'
                        >
                            <Input />
                        </Form.Item>
                    </Col>

                    <Col md={8} xs={24}>
                        <Form.Item label='Situação do lote' name='prl_status'>
                            <Select
                                options={[
                                    { label: 'Ativo', value: ProdutoLoteStatus.Ativo },
                                    {
                                        label: 'Encerrado / Esgotado',
                                        value: ProdutoLoteStatus.Encerrado,
                                    },
                                    {
                                        label: 'Expirado / Vencido',
                                        value: ProdutoLoteStatus.Expirado,
                                    },
                                ]}
                            />
                        </Form.Item>
                    </Col>

                    <Col md={8} xs={24}>
                        <Form.Item label='Data de fabricação' name='prl_data_fabricacao'>
                            <PickDate format={DATE_FORMAT} />
                        </Form.Item>
                    </Col>

                    <Col md={8} xs={24}>
                        <Form.Item
                            label='Data de expiração / vencimento'
                            name='prl_data_vencimento'
                            rules={[
                                {
                                    message: 'Por favor, forneça a data de vencimento',
                                    required: true,
                                },
                            ]}
                        >
                            <PickDate format={DATE_FORMAT} />
                        </Form.Item>
                    </Col>
                </Row>

                {(dialog.state.data?.estoque?.length ?? 0) > 0 && (
                    <ProdutoTabEstoqueLoteModalEstoques
                        estoquesLote={dialog.state.data?.estoque ?? []}
                        locais={locais}
                        produtoEstoque={produtoEstoque.filter((estoque) => estoque.excluir !== 'S')}
                    />
                )}
            </Form>
        </Modal>
    );
}
